import PropTypes from 'prop-types';
import {
  Tooltip,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  Badge,
  Box,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { green, orange, purple, red } from '@mui/material/colors';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

const FLAG_OPTIONS = [
  { value: 'SHORTLISTED', label: 'Shortlisted', color: green[500] },
  { value: 'HOLD', label: 'Hold', color: orange[500] },
  { value: 'FOLLOW_UP', label: 'Follow up', color: purple[500] },
  { value: 'REJECT', label: 'Reject', color: red[500] },
];

export default function JobCandidateFlagFilter({
  flagFilter,
  setFlagFilter,
  flagCounts,
  totalFlaggedCount,
  isRegularFilterActive,
}) {
  const flagPopover = usePopover();

  if (isRegularFilterActive) return null;

  if (!totalFlaggedCount) {
    return (
      <Tooltip title="No candidates have been flagged yet">
        <span>
          <IconButton disabled variant="outlined">
            <Iconify icon="iconamoon:flag-light" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const activeOption = FLAG_OPTIONS.find(option => option.value === flagFilter);

  const tooltipTitle = flagFilter
    ? `Showing: ${activeOption.label} — click to change or clear`
    : 'Filter by flag';

  const handleSelect = value => {
    setFlagFilter(prev => (prev === value ? null : value));
    flagPopover.onClose();
  };

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          variant="outlined"
          onClick={flagPopover.onOpen}
          sx={{
            borderColor: theme =>
              (flagPopover.open || flagFilter) && theme.palette.info.main,
          }}
        >
          <Badge badgeContent={!flagFilter ? totalFlaggedCount : 0} color="error">
            <Iconify
              icon={flagFilter ? 'iconamoon:flag-fill' : 'iconamoon:flag-light'}
              color={flagFilter ? activeOption.color : 'black'}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <CustomPopover
        open={flagPopover.open}
        onClose={flagPopover.onClose}
        sx={{ width: 260 }}
      >
        <Typography variant="paragraph" fontWeight={600} sx={{ mb: '12px' }}>
          Filter by flag
        </Typography>

        {FLAG_OPTIONS.map(option => (
          <MenuItem
            key={option.value}
            selected={flagFilter === option.value}
            onClick={() => handleSelect(option.value)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ minWidth: '28px' }}>
                  <Iconify icon="iconamoon:flag-fill" color={option.color} />
                </ListItemIcon>
                <ListItemText primary={option.label} />
              </Box>
              <Stack direction="row" alignItems="center" spacing="8px" sx={{ ml: '12px', flexShrink: 0 }}>
                <Typography variant="caption">
                  {flagCounts[option.value] || 0}
                </Typography>
                {flagFilter === option.value && (
                  <Iconify icon="eva:checkmark-fill" color="black" />
                )}
              </Stack>
            </Box>
          </MenuItem>
        ))}

        {flagFilter && (
          <MenuItem onClick={() => handleSelect(flagFilter)}>
            <Typography variant="input" color="error">
              Clear filter
            </Typography>
          </MenuItem>
        )}
      </CustomPopover>
    </>
  );
}

JobCandidateFlagFilter.propTypes = {
  flagFilter: PropTypes.string,
  setFlagFilter: PropTypes.func.isRequired,
  flagCounts: PropTypes.object.isRequired,
  totalFlaggedCount: PropTypes.number.isRequired,
  isRegularFilterActive: PropTypes.bool.isRequired,
};
